import { includes } from 'lodash';
import { useCallback, useState } from 'react';
import { create } from 'zustand';

import type { SoftwareUpdateData } from '@sb/feathers-types';
import { makeNamespacedLog } from '@sb/log';
import { wait } from '@sb/utilities';
import { getAllJointNumbersExceptIOBoard } from '@sb/utilities/src/joints';
import { useFeatureFlag } from '@sbrc/hooks';
import {
  getSoftwareUpdateData,
  requestSoftwareInstall,
  markWarningAsNotified as markWarningAsNotifiedService,
} from '@sbrc/services';

const log = makeNamespacedLog('useSoftwareBuildData');
// This BUILD_ID is the same marvin Build ID.
// The Github build workflow will pass this as an argument to the docker file.
// That way the build ID gets "baked in" to the image.
// For local development this won't be set, so we use 'active' as the default.
// 'active' matches the default build ID in local-build-data/read-and-write.ts
export const BUILD_ID = import.meta.env.VITE_BUILD_ID || 'active';

export interface SoftwareBuildData extends Partial<SoftwareUpdateData> {
  timeChecked: Date | null;
  hasRequestedSoftwareInstall: boolean;
  isInstallingSoftware: boolean;
  onRequestSoftwareInstall: (
    offlineUpdatePath?: string,
    buildId?: string,
    buildName?: string,
  ) => void;
  selectedJointsForUpdate: number[];
  setSelectedJointsForUpdate: (selectedJoints: number[]) => void;
  isOnlineSoftwareUpdateAvailable: boolean;
  markWarningAsNotified: () => Promise<void>;
}

const INITIAL_BUILD_DATA: SoftwareBuildData = {
  timeChecked: null,
  hasRequestedSoftwareInstall: false,
  isInstallingSoftware: false,
  onRequestSoftwareInstall: () => {},
  selectedJointsForUpdate: [],
  setSelectedJointsForUpdate: () => {},
  isOnlineSoftwareUpdateAvailable: false,
  lastErrorMessage: undefined,
  lastErrorAt: undefined,
  lastWarningMessage: undefined,
  lastWarningNotified: undefined,
  markWarningAsNotified: async () => {},
};

const useBuildData = create(() => INITIAL_BUILD_DATA);

/**
 * poll backend for changes in software build data
 */
export const pollSoftwareBuildData = async () => {
  while (true) {
    try {
      const softwareUpdateData = await getSoftwareUpdateData();

      let { hasRequestedSoftwareInstall } = useBuildData.getState();

      if (
        !softwareUpdateData ||
        softwareUpdateData.isSoftwareInstallRequested ||
        softwareUpdateData.status === 'installing' ||
        softwareUpdateData.status === 'installed'
      ) {
        hasRequestedSoftwareInstall = false;
      }

      const activeBuildID =
        softwareUpdateData?.activeBuildID ??
        // if undefined then feathers-bot is running a different version than remote-control
        // (i.e. during upgrade)
        'version-mismatch';

      // Reload if the active build ID is different from the BUILD_ID
      // This is to ensure that the remote-control is always running the same build as the one
      // that is being installed.
      if (BUILD_ID !== activeBuildID) {
        if (BUILD_ID === 'active') {
          // if BUILD_ID is 'active' and it doesn't match the activeBuildID,
          // then this is probably a developer running a custom remote-control build
          // on a robot. In this case we don't want to reload the page because the
          // active_build.json will continue to have the old build ID.
        } else {
          // Otherwise, this is a production environment and we should reload the page
          // to ensure that the remote-control is always running the same build as the one
          // that is being installed.
          log.info(
            'pollSoftwareBuildData',
            `build ID mismatch: remote-control '${BUILD_ID}' !== active_build.json '${activeBuildID}'`,
          );

          window.location.reload();
        }
      }

      useBuildData.setState({
        ...INITIAL_BUILD_DATA,
        ...softwareUpdateData,
        timeChecked: new Date(),
        hasRequestedSoftwareInstall,
      });

      await wait(1_000);
    } catch (error) {
      log.warn('poll.fail', 'Failed to get SoftwareUpdateData', error);
      await wait(2_000);
    }

    await wait(1_000);
  }
};

export const useSoftwareBuildData = (): SoftwareBuildData => {
  const enableFirmareAutoUpdate = useFeatureFlag('enableFirmwareAutoUpdate');

  const buildData = useBuildData();

  const [selectedJointsForUpdate, setSelectedJointsForUpdate] = useState<
    number[]
  >(getAllJointNumbersExceptIOBoard);

  const onRequestSoftwareInstall = useCallback(
    async (
      offlineUpdatePath?: string,
      buildId?: string,
      buildName?: string,
    ) => {
      useBuildData.setState({ hasRequestedSoftwareInstall: true });

      await requestSoftwareInstall(
        enableFirmareAutoUpdate ? selectedJointsForUpdate : [],
        offlineUpdatePath,
        buildId,
        buildName,
      );
    },
    [enableFirmareAutoUpdate, selectedJointsForUpdate],
  );

  const markWarningAsNotified = useCallback(async () => {
    useBuildData.setState({ lastWarningNotified: true });

    // Also update the backend to persist the change
    try {
      await markWarningAsNotifiedService();
    } catch (error) {
      log.warn(
        'markWarningAsNotified.fail',
        'Failed to mark warning as notified on backend',
        error,
      );
    }
  }, []);

  const isInstallingSoftware =
    buildData.hasRequestedSoftwareInstall ||
    buildData.isSoftwareInstallRequested ||
    buildData.status === 'installing';

  let isOnlineSoftwareUpdateAvailable: boolean =
    buildData.targetBuildID !== 'Unknown' &&
    buildData.targetBuildID !== buildData.activeBuildID &&
    !includes(buildData.offlineUpdatePath, buildData.targetBuildID);

  const newOperationMode =
    buildData.targetOperationMode !== undefined &&
    buildData.targetOperationMode !== buildData.activeOperationMode;

  if (newOperationMode) {
    isOnlineSoftwareUpdateAvailable = true;
  }

  return {
    ...buildData,
    onRequestSoftwareInstall,
    selectedJointsForUpdate,
    setSelectedJointsForUpdate,
    isInstallingSoftware,
    isOnlineSoftwareUpdateAvailable,
    markWarningAsNotified,
  };
};
